import { useNavigate } from "react-router-dom";
import images from "../assets/images";
import { useEffect, useState, useRef } from 'react';
import axiosInstance from "../Axios";
import Toast from "../services/Toast";
import Const from "../config/const";
import Loader from "./loader/Loader";
import './Navbar.css';

const Navbar: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState<any>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!user) {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                setUser(JSON.parse(storedUser));
            }
        }
    }, [user]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const openLogoutConfirm = () => {
        setIsDropdownOpen(false);
        setIsLogoutConfirmOpen(true);
    };

    const cancelLogout = () => {
        setIsLogoutConfirmOpen(false);
    };

    const logout = async () => {
        try {
            setIsLogoutConfirmOpen(false);
            setLoading(true);
            const response = await axiosInstance.get('/auth/logout');
            setLoading(false);
            if (!response?.data?.status) {
                Toast.error(response.data.message);
                return;
            }
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            Toast.success("Signed out successfully.");
            // Hand off to the app's logout so its session on THIS device ends
            // too — only the browser can end it, since the app keys its session
            // to a cookie. from_sso tells it we already ended the SSO session,
            // so it skips revoking it again and redirects back here with
            // ?action=logout, a no-op that lands on the login page.
            //
            // Forward the current URL's ?application= (if any) so linentech can
            // echo it back on the return redirect. The SSO URL is the source of
            // truth for "which app did this session belong to" — using
            // linentech's Auth::check() instead would pick up a stale cookie
            // from an older visit and wrongly send a portal-only user into
            // linentech after re-login.
            if (Const.linentechLogout) {
                const separator = Const.linentechLogout.includes('?') ? '&' : '?';
                const currentApplication = new URLSearchParams(window.location.search).get('application');
                const params = new URLSearchParams({ from_sso: 'true' });
                if (currentApplication) {
                    params.set('application', currentApplication);
                }
                window.location.href = `${Const.linentechLogout}${separator}${params.toString()}`;
                return;
            }
            navigate("/");
        } catch (error) {
            setLoading(false);
        }
    };

    const getInitials = (name: string) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <>
            {/* Full-page logout overlay */}
            {loading && (
                <div className="logout-overlay">
                    <div className="logout-overlay__content">
                        <div className="logout-overlay__spinner">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" strokeOpacity="0.2"/>
                                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <p className="logout-overlay__text">Signing out...</p>
                    </div>
                </div>
            )}
            <nav className="navbar" data-cy="navbar">
            <div className="navbar__brand">
                <a href="/home" data-cy="navbar-logo">
                    <img src={images.headerLogo} alt="Nextek Systems" className="navbar__logo" />
                </a>
            </div>

            <div className="profile-dropdown" ref={dropdownRef}>
                <button
                    className={`profile-dropdown__trigger ${isDropdownOpen ? 'profile-dropdown__trigger--active' : ''}`}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    data-cy="navbar-profile-trigger"
                >
                    <img 
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.user_name || 'User')}&background=0046FF&color=fff&bold=true`}
                        alt="Profile" 
                        className="profile-dropdown__avatar" 
                    />
                    <div className="profile-dropdown__info">
                        <span className="profile-dropdown__name">{user?.user_name || 'User'}</span>
                    </div>
                    <svg className="profile-dropdown__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6"/>
                    </svg>
                </button>

                <div className={`profile-dropdown__menu ${isDropdownOpen ? 'profile-dropdown__menu--open' : ''}`} data-cy="navbar-profile-menu">
                    <div className="profile-dropdown__header">
                        <div className="profile-dropdown__user">
                            <img 
                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.user_name || 'User')}&background=0046FF&color=fff&bold=true&size=96`}
                                alt="Profile" 
                                className="profile-dropdown__user-avatar" 
                            />
                            <div className="profile-dropdown__user-details">
                                <p className="profile-dropdown__user-name">{user?.user_name || 'User'}</p>
                                <p className="profile-dropdown__user-email">{user?.email || ''}</p>
                            </div>
                        </div>
                    </div>

                    <div className="profile-dropdown__items">
                        <button
                            className="profile-dropdown__item profile-dropdown__item--logout"
                            onClick={openLogoutConfirm}
                            data-cy="navbar-signout-btn"
                        >
                            <svg className="profile-dropdown__item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                <polyline points="16 17 21 12 16 7"/>
                                <line x1="21" y1="12" x2="9" y2="12"/>
                            </svg>
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        </nav>

        {isLogoutConfirmOpen && (
            <div className="logout-confirm" role="dialog" aria-modal="true" aria-labelledby="logout-confirm-title" data-cy="logout-confirm-dialog">
                <div className="logout-confirm__backdrop" onClick={cancelLogout} />
                <div className="logout-confirm__dialog">
                    <div className="logout-confirm__icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                    </div>
                    <h3 id="logout-confirm-title" className="logout-confirm__title">Sign out of all applications?</h3>
                    <p className="logout-confirm__message">
                        Signing out here will also sign you out of every application connected through Single Sign-On. You will need to sign in again to continue using them.
                    </p>
                    <div className="logout-confirm__actions">
                        <button
                            type="button"
                            className="logout-confirm__btn logout-confirm__btn--cancel"
                            onClick={cancelLogout}
                            data-cy="logout-confirm-cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="logout-confirm__btn logout-confirm__btn--confirm"
                            onClick={logout}
                            data-cy="logout-confirm-signout-btn"
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default Navbar;
